import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../Navbar/Navbar";
import Bredcom from "../Bredcom/Bredcom";
import Footer from "../Footer/Footer";
import SurveyResultsModal from "./SurveyResultsModal";
import { SURVEYS, SURVEY_CATEGORIES } from "./surveysData";

function SectionHeader({ eyebrow, title, lead }) {
  return (
    <div className="surveys-section-header">
      <span className="surveys-section-header__eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      <p>{lead}</p>
    </div>
  );
}

function getResultModeLabel(resultMode, meta) {
  if (resultMode === "narrative") return meta.available;
  if (resultMode === "internal") return meta.internal;
  return meta.pending;
}

function Ankete() {
  const { t } = useTranslation();
  const page = t("surveysPage", { returnObjects: true });

  const [activeSurveyId, setActiveSurveyId] = useState(null);

  const activeSurveyIndex = useMemo(
    () => SURVEYS.findIndex((survey) => survey.id === activeSurveyId),
    [activeSurveyId],
  );

  const activeSurvey =
    activeSurveyIndex >= 0 ? SURVEYS[activeSurveyIndex] : null;

  const groupedSurveys = useMemo(() => {
    return SURVEY_CATEGORIES.map((category) => ({
      ...category,
      items: SURVEYS.filter((survey) => survey.category === category.id),
    }));
  }, []);

  const openSurvey = (surveyId) => {
    setActiveSurveyId(surveyId);
  };

  const closeSurvey = () => {
    setActiveSurveyId(null);
  };

  const goPrev = () => {
    if (activeSurveyIndex > 0) {
      setActiveSurveyId(SURVEYS[activeSurveyIndex - 1].id);
    }
  };

  const goNext = () => {
    if (activeSurveyIndex < SURVEYS.length - 1) {
      setActiveSurveyId(SURVEYS[activeSurveyIndex + 1].id);
    }
  };

  return (
    <>
      <Navbar />
      <Bredcom
        title={t("surveyPage.breadcrumbHome")}
        subtitle={t("surveyPage.hero.title")}
      />

      <main className="surveys-page">
        <div className="container surveys-page__container">
          <section className="surveys-hero">
            <div className="surveys-hero__content">
              <span className="surveys-hero__eyebrow">{page.heroEyebrow}</span>
              <h1>{page.heroTitle}</h1>
              <p>{page.heroText}</p>

              <div className="surveys-hero__badges">
                {page.heroBadges.map((badge) => (
                  <span
                    key={badge}
                    className="survey-pill survey-pill--neutral"
                  >
                    {badge}
                  </span>
                ))}
              </div>

              <div className="surveys-hero__actions">
                <Link
                  to="/obezbjedjivanje-kvaliteta"
                  className="survey-btn survey-btn--primary"
                >
                  {page.primaryAction}
                  <i className="fas fa-arrow-left" />
                </Link>

                <a href="#ankete-grid" className="survey-btn survey-btn--ghost">
                  {page.secondaryAction}
                  <i className="fas fa-arrow-down" />
                </a>
              </div>
            </div>

            <div className="surveys-hero__stats">
              {page.summaryCards.map((card) => (
                <article key={card.label} className="surveys-stat-card">
                  <span className="surveys-stat-card__label">{card.label}</span>
                  <strong>{card.value}</strong>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="surveys-surface surveys-intro">
            <div className="row">
              <div className="col-lg-8">
                <SectionHeader
                  eyebrow={page.intro.eyebrow}
                  title={page.intro.title}
                  lead={page.intro.lead}
                />
                <div className="surveys-rich-text">
                  {page.intro.paragraphs.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              </div>

              <div className="col-lg-4">
                <div className="surveys-note-card">
                  <span className="surveys-note-card__eyebrow">
                    {page.notice.eyebrow}
                  </span>
                  <h3>{page.notice.title}</h3>
                  <p>{page.notice.text}</p>
                </div>
              </div>
            </div>
          </section>

          <div id="ankete-grid" className="surveys-groups">
            {groupedSurveys.map((group) => {
              const sectionCopy = page.sections[group.id];

              return (
                <section key={group.id} className="surveys-group">
                  <SectionHeader
                    eyebrow={sectionCopy.eyebrow}
                    title={sectionCopy.title}
                    lead={sectionCopy.lead}
                  />

                  <div className="surveys-card-grid">
                    {group.items.map((survey) => {
                      const surveyCopy = page.items[survey.id];
                      const modeLabel = getResultModeLabel(
                        survey.resultMode,
                        page.meta,
                      );

                      return (
                        <article key={survey.id} className="survey-card">
                          <div className="survey-card__top">
                            <div className="survey-card__icon">
                              <i className={survey.icon} />
                            </div>
                            <div>
                              <h3>{surveyCopy.title}</h3>
                              <p>{surveyCopy.short}</p>
                            </div>
                          </div>

                          <button
                            type="button"
                            className="survey-card__imageButton"
                            onClick={() => openSurvey(survey.id)}
                            aria-label={`${page.actions.viewResults}: ${surveyCopy.title}`}
                          >
                            <div className="survey-card__image">
                              <img src={survey.image} alt={surveyCopy.title} />
                              <div className="survey-card__imageOverlay">
                                <span>{page.modal.imageHint}</span>
                              </div>
                            </div>
                          </button>

                          <div className="survey-card__focus">
                            {surveyCopy.focus.map((item) => (
                              <span key={item} className="survey-focus-tag">
                                {item}
                              </span>
                            ))}
                          </div>

                          <div className="survey-card__badges">
                            <span className="survey-pill survey-pill--primary">
                              {modeLabel}
                            </span>
                            <span className="survey-pill survey-pill--neutral">
                              {page.meta.periodic}
                            </span>
                            {survey.isCommissionRecommendation && (
                              <span className="survey-pill survey-pill--accent">
                                {page.meta.commission}
                              </span>
                            )}
                          </div>

                          <div className="survey-card__footer">
                            <p>{surveyCopy.cardNote}</p>
                            <button
                              type="button"
                              className="survey-btn survey-btn--primary survey-btn--full"
                              onClick={() => openSurvey(survey.id)}
                            >
                              {page.actions.viewResults}
                              <i className="fas fa-chart-bar" />
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </main>

      <SurveyResultsModal
        isOpen={Boolean(activeSurvey)}
        survey={activeSurvey}
        surveyCopy={activeSurvey ? page.items[activeSurvey.id] : null}
        meta={page.meta}
        modalCopy={page.modal}
        actions={page.actions}
        onClose={closeSurvey}
        onPrev={goPrev}
        onNext={goNext}
        hasPrev={activeSurveyIndex > 0}
        hasNext={
          activeSurveyIndex > -1 && activeSurveyIndex < SURVEYS.length - 1
        }
      />

      <Footer />
    </>
  );
}

export default Ankete;
